import React from 'react';
import i18n from 'i18next';
import { Helmet } from 'react-helmet';
import { useEffect, useState } from 'react';
import { SupportedLanguages } from '../../enums';

const EN_LOCALE = 'en_US';
const DE_LOCALE = 'de_DE';

const Head = ({
  title,
  description,
  cardTitle,
  cardDescription,
  cardCreatorTwitterAccount,
  siteTwitterAccount,
  twitterCardType = 'summary_large_image',
  imgUrl,
  imgUrlSecure,
  imgAlt,
  imgType = 'png',
  imgHeight = 630,
  imgWidth = 1200,
  // structuredDataJSON,
  ogUrl,
  ogType = 'website',
}: HeadProps) => {
  const [locale, setLocale] = useState(EN_LOCALE);
  const [localeArray, setLocaleArray] = useState([DE_LOCALE]);

  useEffect(() => {
    switch (i18n.language) {
      case SupportedLanguages.en:
        setLocale(EN_LOCALE);
        setLocaleArray([DE_LOCALE]);
        break;
      case SupportedLanguages.de:
      default:
        setLocale(DE_LOCALE);
        setLocaleArray([EN_LOCALE]);
        break;
    }
  }, []);

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {/* ----------Open Graph---------- */}
      <meta property="og:title" content={cardTitle || title} />
      <meta property="og:description" content={cardDescription || description} />
      <meta property="og:site_name" content="Repair Rebels" />
      {/* Website, article, book, profile, video or music */}
      {ogType && <meta property="og:type" content={ogType} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {/*<meta property="og:determiner" content="" />*/}

      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={localeArray[0]} />

      {/* 1200x630 */}
      {(imgUrl || imgUrlSecure) && <meta property="og:image" content={imgUrl || imgUrlSecure} />}
      {imgUrlSecure && <meta property="og:image:secure_url" content={imgUrlSecure} />}
      {imgType && <meta property="og:image:type" content={`image/${imgType}`} />}
      {imgWidth && <meta property="og:image:width" content={imgWidth.toString()} />}
      {imgHeight && <meta property="og:image:height" content={imgHeight.toString()} />}
      {(imgUrl || imgUrlSecure) && <meta property="og:image:alt" content={imgAlt || title} />}

      {/* ----------Twitter Cards---------- */}

      {/* summary, summary_large_image, player, app */}
      {twitterCardType && <meta name="twitter:card" content={twitterCardType} />}
      {siteTwitterAccount && <meta name="twitter:site" content={siteTwitterAccount} />}
      {cardCreatorTwitterAccount && <meta name="twitter:creator" content={cardCreatorTwitterAccount} />}
      {/* MAX: 70 chars */}
      <meta name="twitter:title" content={cardTitle ?? title} />
      {/* MAX: 200 chars */}
      <meta name="twitter:description" content={cardDescription ?? description} />
      {/* PNG, GIF, JPG, WEBP < 5MB | 1200x630 */}
      {(imgUrl || imgUrlSecure) && <meta name="twitter:image" content={imgUrl ?? imgUrlSecure} />}
      {/* MAX: 420 chars */}
      {(imgUrl || imgUrlSecure) && <meta name="twitter:image:alt" content={imgAlt ?? title} />}

      {/* Structured Data */}
      {/*{structuredDataJSON && <script type="application/ld+json">{JSON.stringify(structuredDataJSON)}</script>}*/}
    </Helmet>
  );
};

type HeadProps = {
  cardCreatorTwitterAccount?: string;
  cardDescription?: string;
  cardTitle?: string;
  description?: string;
  imgAlt?: string;
  imgHeight?: number;
  imgType?: string;
  imgUrl?: string;
  imgUrlSecure?: string;
  imgWidth?: number;
  ogType?: string;
  ogUrl?: string;
  siteTwitterAccount?: string;
  // structuredDataJSON: any;
  title?: string;
  twitterCardType?: string;
};

export { Head };
